import { Button } from "@devhop/ui";
import {
  Html5Qrcode,
  type CameraDevice,
  type Html5QrcodeCameraScanConfig,
} from "html5-qrcode";
import {
  CameraIcon,
  CameraOffIcon,
  SwitchCameraIcon,
} from "lucide-react";
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

type FacingMode = "environment" | "user";

const START_TIMEOUT_MS = 8000;

function normalizeScannedCode(raw: string): string {
  const trimmed = raw.trim();
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const url = new URL(trimmed);
      const fromQuery =
        url.searchParams.get("code") ?? url.searchParams.get("redeem");
      if (fromQuery) return fromQuery.trim().toUpperCase();
      const last = url.pathname.split("/").filter(Boolean).pop();
      if (last) return decodeURIComponent(last).trim().toUpperCase();
    }
  } catch {
    // fall through
  }
  return trimmed.toUpperCase();
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string) {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(`${label} timeout`));
    }, ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function classifyFacing(label: string): FacingMode | null {
  const text = label.toLowerCase();
  if (/front|user|facing\s*front|ໜ້າ/.test(text)) return "user";
  if (/back|rear|environment|facing\s*back|หลัง|ຫຼັງ/.test(text)) {
    return "environment";
  }
  return null;
}

function isSecondaryLens(label: string) {
  return /tele|ultra|wide|depth|macro|infrared|ir\b|tof|logical/i.test(label);
}

function pickCameraIdForFacing(
  cameras: CameraDevice[],
  facing: FacingMode,
): string | null {
  if (cameras.length === 0) return null;

  const matched = cameras.filter(
    (cam) => classifyFacing(cam.label) === facing,
  );

  if (matched.length > 0) {
    const primary = matched.find((cam) => !isSecondaryLens(cam.label));
    return primary?.id ?? matched[0]?.id ?? null;
  }

  if (cameras.length === 1) return cameras[0]?.id ?? null;

  if (facing === "user") {
    return cameras[0]?.id ?? null;
  }

  // ไม่มี label: บนมือถือกล้องหลังมักไม่ใช่ตัวแรก
  const nonFront = cameras.filter(
    (cam) => classifyFacing(cam.label) !== "user" && !isSecondaryLens(cam.label),
  );
  if (nonFront.length > 0) {
    return nonFront[nonFront.length - 1]?.id ?? null;
  }
  return cameras[cameras.length - 1]?.id ?? null;
}

async function teardownScanner(scanner: Html5Qrcode | null) {
  if (!scanner) return;
  try {
    if (scanner.isScanning) {
      await scanner.stop();
    }
  } catch {
    // ignore
  }
  try {
    scanner.clear();
  } catch {
    // ignore
  }
}

function scanConfig(): Html5QrcodeCameraScanConfig {
  return {
    fps: 8,
    qrbox: (viewfinderWidth, viewfinderHeight) => {
      const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
      const edge = Math.max(
        50,
        Math.min(250, Math.floor(minEdge > 0 ? minEdge * 0.7 : 200)),
      );
      return { width: edge, height: edge };
    },
  };
}

export type VisitorQrCameraScannerHandle = {
  stop: () => Promise<void>;
};

type VisitorQrCameraScannerProps = {
  onScan: (code: string) => void;
  disabled?: boolean;
};

export const VisitorQrCameraScanner = forwardRef<
  VisitorQrCameraScannerHandle,
  VisitorQrCameraScannerProps
>(function VisitorQrCameraScanner({ onScan, disabled }, ref) {
  const regionId = useId().replace(/:/g, "");
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScanRef = useRef<{ code: string; at: number } | null>(null);
  const onScanRef = useRef(onScan);
  const facingRef = useRef<FacingMode>("environment");
  const cancelledRef = useRef(false);

  const [active, setActive] = useState(false);
  const [starting, setStarting] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canSwitchCamera, setCanSwitchCamera] = useState(false);
  const [facing, setFacing] = useState<FacingMode>("environment");

  onScanRef.current = onScan;

  const startWithSource = async (
    source: string | MediaTrackConstraints,
  ) => {
    if (cancelledRef.current) {
      throw new Error("cancelled");
    }

    // เคลียร์ instance เก่าก่อนสร้างใหม่ — กันค้างจาก stream ค้าง
    const previous = scannerRef.current;
    scannerRef.current = null;
    await teardownScanner(previous);

    const scanner = new Html5Qrcode(regionId);
    scannerRef.current = scanner;

    await withTimeout(
      scanner.start(
        source,
        scanConfig(),
        (decoded) => {
          const code = normalizeScannedCode(decoded);
          if (!code) return;
          const now = Date.now();
          const last = lastScanRef.current;
          if (last && last.code === code && now - last.at < 2500) return;
          lastScanRef.current = { code, at: now };
          onScanRef.current(code);
        },
        () => undefined,
      ),
      START_TIMEOUT_MS,
      "camera start",
    );
  };

  /**
   * ลำดับที่เสถียรบนมือถือ:
   * 1) getCameras → เปิดด้วย deviceId
   * 2) fallback facingMode แบบ string เท่านั้น (html5-qrcode ไม่รองรับ ideal)
   */
  const startFacing = async (facing: FacingMode) => {
    let cameras: CameraDevice[] = [];
    try {
      cameras = await withTimeout(
        Html5Qrcode.getCameras(),
        START_TIMEOUT_MS,
        "getCameras",
      );
    } catch {
      cameras = [];
    }

    if (cancelledRef.current) throw new Error("cancelled");

    setCanSwitchCamera(cameras.length > 1);

    const cameraId = pickCameraIdForFacing(cameras, facing);
    const attempts: Array<string | MediaTrackConstraints> = [];

    if (cameraId) attempts.push(cameraId);
    // string เท่านั้น — object { ideal } ทำให้ html5-qrcode พัง/ค้าง
    attempts.push({ facingMode: facing });

    // ถ้าต้องการหลัง แต่ deviceId อาจเป็น front ผิด → ลอง facingMode ก่อนเมื่อไม่มี label
    const hasLabeledBack = cameras.some(
      (cam) => classifyFacing(cam.label) === "environment",
    );
    if (facing === "environment" && !hasLabeledBack && cameras.length > 1) {
      // ใส่ facingMode ขึ้นก่อน deviceId ที่เดา
      attempts.length = 0;
      attempts.push({ facingMode: facing });
      if (cameraId) attempts.push(cameraId);
      const last = cameras[cameras.length - 1]?.id;
      if (last && last !== cameraId) attempts.push(last);
    }

    let lastError: unknown;
    for (const source of attempts) {
      if (cancelledRef.current) throw new Error("cancelled");
      try {
        await startWithSource(source);
        return;
      } catch (err) {
        lastError = err;
        const scanner = scannerRef.current;
        scannerRef.current = null;
        await teardownScanner(scanner);
        // รอสั้น ๆ ให้ OS ปล่อยกล้องก่อนลองตัวถัดไป
        await new Promise((r) => setTimeout(r, 250));
      }
    }

    throw lastError ?? new Error("No camera available");
  };

  const stopCamera = async () => {
    cancelledRef.current = true;
    const scanner = scannerRef.current;
    scannerRef.current = null;
    await teardownScanner(scanner);
    setActive(false);
    setStarting(false);
    setSwitching(false);
  };

  useImperativeHandle(ref, () => ({
    stop: stopCamera,
  }));

  useEffect(() => {
    if (!starting || active) return;

    cancelledRef.current = false;
    let cancelled = false;

    const run = async () => {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      if (cancelled || cancelledRef.current) return;

      try {
        const preferred = facingRef.current;
        await startFacing(preferred);
        if (cancelled || cancelledRef.current) {
          const scanner = scannerRef.current;
          scannerRef.current = null;
          await teardownScanner(scanner);
          return;
        }

        setFacing(preferred);
        setActive(true);
        setStarting(false);
      } catch (err) {
        if (cancelled || cancelledRef.current) return;
        scannerRef.current = null;
        const message =
          err instanceof Error ? err.message : "ບໍ່ສາມາດເປີດກ້ອງໄດ້";
        if (/cancelled/i.test(message)) {
          // user ยกเลิก
        } else if (/timeout/i.test(message)) {
          setError("ເປີດກ້ອງນານເກີນໄປ — ລອງໃໝ່ ຫຼື ສະຫຼັບກ້ອງ");
        } else if (/NotAllowedError|Permission/i.test(message)) {
          setError("ບໍ່ໄດ້ຮັບອະນຸຍາດໃຊ້ກ້ອງ — ກະລຸນາອະນຸຍາດໃນໂທລະສັບ");
        } else if (/NotFoundError|Requested device not found/i.test(message)) {
          setError("ບໍ່ພົບກ້ອງໃນເຄື່ອງນີ້");
        } else {
          setError("ເປີດກ້ອງບໍ່ສຳເລັດ — ລອງໃໝ່ ຫຼື ພິມໂຄດແທນ");
        }
        setActive(false);
        setStarting(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
      cancelledRef.current = true;
    };
  }, [starting, active, regionId]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      const scanner = scannerRef.current;
      scannerRef.current = null;
      void teardownScanner(scanner);
    };
  }, []);

  const switchCamera = async () => {
    if (!active || switching) return;
    setSwitching(true);
    setError(null);
    cancelledRef.current = false;

    const nextFacing: FacingMode =
      facingRef.current === "environment" ? "user" : "environment";

    const scanner = scannerRef.current;
    scannerRef.current = null;
    await teardownScanner(scanner);
    await new Promise((r) => setTimeout(r, 300));

    try {
      facingRef.current = nextFacing;
      await startFacing(nextFacing);
      setFacing(nextFacing);
    } catch {
      try {
        facingRef.current =
          nextFacing === "environment" ? "user" : "environment";
        await startFacing(facingRef.current);
        setFacing(facingRef.current);
        setError("ສະຫຼັບກ້ອງບໍ່ສຳເລັດ — ກັບໄປກ້ອງເດີມ");
      } catch {
        setError("ສະຫຼັບກ້ອງບໍ່ສຳເລັດ");
        setActive(false);
      }
    } finally {
      setSwitching(false);
    }
  };

  const showPreview = starting || active;
  const facingLabel = facing === "environment" ? "ກ້ອງຫຼັງ" : "ກ້ອງໜ້າ";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {!active && !starting ? (
          <Button
            type="button"
            variant="secondary"
            disabled={disabled}
            onClick={() => {
              setError(null);
              cancelledRef.current = false;
              facingRef.current = "environment";
              setFacing("environment");
              setStarting(true);
            }}
          >
            <CameraIcon className="mr-2 size-4" />
            ເປີດກ້ອງສະແກນ
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => void stopCamera()}
            >
              <CameraOffIcon className="mr-2 size-4" />
              {starting && !active ? "ຍົກເລີກ" : "ປິດກ້ອງ"}
            </Button>
            {active ? (
              <Button
                type="button"
                variant="secondary"
                disabled={switching || disabled}
                onClick={() => void switchCamera()}
              >
                <SwitchCameraIcon className="mr-2 size-4" />
                {switching
                  ? "ກຳລັງສະຫຼັບ..."
                  : canSwitchCamera
                    ? `ສະຫຼັບ (${facingLabel})`
                    : "ສະຫຼັບກ້ອງ"}
              </Button>
            ) : null}
          </>
        )}
        <p className="text-muted-foreground text-xs">ຫຼື ພິມໂຄດດ້ານລຸ່ມ</p>
      </div>

      {error ? (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      ) : null}

      <div
        id={regionId}
        className={
          showPreview
            ? "min-h-[240px] w-full overflow-hidden rounded-lg border bg-black [&_video]:h-auto [&_video]:max-h-[min(70vw,22rem)] [&_video]:w-full [&_video]:object-contain"
            : "hidden"
        }
      />

      {showPreview ? (
        <p className="text-muted-foreground text-center text-xs">
          {starting && !active
            ? "ກຳລັງເປີດກ້ອງ..."
            : "ຈັບ QR ໃຫ້ຢູ່ໃນກອບ — ລະບົບຈະຄົ້ນຫາໂຄດອັດຕະໂນມັດ"}
          {active ? ` · ກຳລັງໃຊ້${facingLabel}` : null}
        </p>
      ) : null}
    </div>
  );
});
